import * as Dialog from "@radix-ui/react-dialog";
import { Immutable } from "immer";
import React from "react";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { getFocusableElems } from "../../../utils/keyboardFocus";
import {
    FINANCING_MODAL_TITLE_ID,
    FinancingModalHeader,
} from "../FinancingModalHeader";
import { returnFocusToFinancingModalTrigger } from "../financingModalFocus";

import styles from "./index.module.scss";

export const FinancingRoot = Dialog.Root;

export const FinancingPortalClose = Dialog.Close;

const focusFirstInDialog = (currentTarget: EventTarget | null): void => {
    if (!(currentTarget instanceof HTMLElement)) {
        return;
    }
    getFocusableElems(currentTarget, {
        includeExplicitTabindex: true,
    })[0]?.focus();
};

export const FinancingPortal = (
    props: React.PropsWithChildren<
        Immutable<{
            title: string;
        }>
    >,
): React.JSX.Element => {
    return (
        <Dialog.Portal>
            <Dialog.Overlay className={styles.overlay} />
            <Dialog.Content
                className={styles.container}
                aria-describedby={undefined}
                aria-labelledby={FINANCING_MODAL_TITLE_ID}
                aria-modal={true}
                onOpenAutoFocus={(event) => {
                    // Prefer a real control over the dialog shell (tabIndex=-1)
                    // which Allyant flagged for Shift+Tab trapping (A360 #386).
                    event.preventDefault();
                    focusFirstInDialog(event.currentTarget);
                }}
                onFocus={(event) => {
                    // If focus ever lands on the dialog shell (click / race),
                    // bounce to a real control. Tab wrapping is owned by
                    // onKeyDownCapture — do not infer direction from relatedTarget.
                    if (event.target !== event.currentTarget) {
                        return;
                    }
                    focusFirstInDialog(event.currentTarget);
                }}
                onKeyDownCapture={(event) => {
                    // Own the full Tab cycle so the browser never parks on the
                    // dialog shell / scrollport (A360 #386). Live retest showed
                    // Radix edge-wrap alone is not enough even with scroll on
                    // .wrapper and aria-disabled Submit.
                    if (event.key !== "Tab") {
                        return;
                    }
                    const container = event.currentTarget;
                    if (!(container instanceof HTMLElement)) {
                        return;
                    }
                    const focusables = getFocusableElems(container, {
                        includeExplicitTabindex: true,
                    });
                    if (focusables.length === 0) {
                        return;
                    }
                    const active = getDocument()?.activeElement;
                    let index = focusables.findIndex((el) => el === active);
                    if (index === -1) {
                        index = event.shiftKey ? 0 : focusables.length - 1;
                    }
                    const nextIndex = event.shiftKey
                        ? (index - 1 + focusables.length) % focusables.length
                        : (index + 1) % focusables.length;
                    event.preventDefault();
                    event.stopPropagation();
                    focusables[nextIndex]?.focus();
                }}
                onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    returnFocusToFinancingModalTrigger();
                }}
            >
                <FinancingModalHeader title={props.title} />
                <div className={styles.wrapper}>{props.children}</div>
            </Dialog.Content>
        </Dialog.Portal>
    );
};
